import type { ReactElement } from "react"
import styled from "styled-components"
import { audienceLabel } from "~/features/audience"
import { usePocOptions } from "~/features/poc"
import { type AudienceTone, audience, type SectionId, sectionTone } from "~/shared/theme/tokens"
import { CONTENT_TYPE_OPTIONS } from "../filters"

const Root = styled.aside`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background-color: ${({ theme }) => theme.color.surface};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textSecondary};
`

const Group = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
`

const Key = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.color.textPrimary};
`

/** The block colour itself, at the same size the provenance key uses, so the two bars read as a pair. */
const Swatch = styled.span<{ $tone: AudienceTone }>`
  width: ${({ theme }) => theme.spacing.lg};
  height: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ $tone }) => $tone.block};
`

/** Ends a row by saying what that row's colours decide. */
const Rule = styled.span`
  color: ${({ theme }) => theme.color.textSecondary};
`

/**
 * The ministries, in the colours a card wears when the classifier placed one.
 *
 * Kids and Students share Teal Wonder, so they share a swatch: two identical
 * chips under different names would claim a distinction the page cannot show.
 */
const MINISTRY_KEYS: readonly { readonly id: string; readonly label: string; readonly tone: AudienceTone }[] = [
  { id: "kids-students", label: `${audienceLabel("kids")} & ${audienceLabel("students")}`, tone: audience.kids },
  { id: "women", label: audienceLabel("women"), tone: audience.women },
  { id: "men", label: audienceLabel("men"), tone: audience.men },
]

/**
 * Every section, named as the filter above the cards names it — the customer has
 * just read those words in `Filter by`, and a key that renamed them would be a
 * second vocabulary for the same seven buckets.
 */
const SECTION_KEYS: readonly { readonly id: SectionId; readonly label: string }[] = CONTENT_TYPE_OPTIONS.flatMap(
  option => (option.value === "all" ? [] : [{ id: option.value, label: option.label }]),
)

/**
 * The key to the card colours, shown only while `cardColor` is on.
 *
 * Colour-coding says nothing until someone is told what the code is: a page of
 * red, teal and lavender blocks reads as decoration, and the claim it is making
 * — that you can find a Women's study without reading a title — is only testable
 * once the room can see the mapping. So the key travels with the switch, the way
 * the provenance legend travels with the data-source overlay.
 */
export const CardColorLegend = (): ReactElement | null => {
  const { cardColor } = usePocOptions()

  if (!cardColor) {
    return null
  }

  return (
    <Root data-qa-hook="card-color-legend">
      <Group data-qa-hook="card-color-legend-ministries">
        {MINISTRY_KEYS.map(key => (
          <Key key={key.id} data-qa-hook={`card-color-legend-${key.id}`}>
            <Swatch $tone={key.tone} aria-hidden="true" />
            {key.label}
          </Key>
        ))}
        <Rule>A card takes its ministry's colour where the title names one.</Rule>
      </Group>

      <Group data-qa-hook="card-color-legend-sections">
        {SECTION_KEYS.map(key => (
          <Key key={key.id} data-qa-hook={`card-color-legend-${key.id}`}>
            <Swatch $tone={sectionTone[key.id]} aria-hidden="true" />
            {key.label}
          </Key>
        ))}
        <Rule>Everything else takes the colour of the section it is in.</Rule>
      </Group>
    </Root>
  )
}
